import React, { Dispatch, SetStateAction, useState } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { ExerciseDay } from '../../../types/Fisiofit/routine';
import { RoutineForm } from '../validators';

interface UseRoutineDaysOptions {
  form: RoutineForm;
  setForm: Dispatch<SetStateAction<RoutineForm>>;
  expandedExerciseIds: Record<string, boolean>;
  setExpandedExerciseIds: Dispatch<SetStateAction<Record<string, boolean>>>;
}

export const useRoutineDays = ({ form, setForm, expandedExerciseIds, setExpandedExerciseIds }: UseRoutineDaysOptions) => {
  // Estado para controlar el día que se está editando
  const [editingDayId, setEditingDayId] = useState<string | null>(null);
  const [editingDayName, setEditingDayName] = useState<string>('');

  const handleAddDay = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Calcular el número del nuevo día
      const newDayNumber = form.exerciseDays?.length + 1 || 1;

      // Crear una copia profunda de los días existentes
      const updatedDays = form.exerciseDays?.map((day) => ({ ...day })) || [];

      // Añadir el nuevo día con el nombre correcto
      updatedDays.push({
        dayNumber: newDayNumber,
        exercises: [],
        id: `day-${newDayNumber}`,
        name: `Día ${newDayNumber}`,
      });

      // Actualizar el estado completo del formulario
      const newForm = {
        ...form,
        exerciseDays: updatedDays,
      };
      setForm(newForm);
    } catch (error) {
      // Error al añadir día
    }
  };

  const handleRemoveDay = (dayNumber: number) => {
    confirmDialog({
      message: '¿Estás seguro de que quieres eliminar este día? Se perderán todos los ejercicios asociados.',
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'No, volver',
      accept: () => {
        try {
          // Filtrar los días excluyendo el que queremos eliminar
          const filteredDays = form.exerciseDays.filter((d) => d.dayNumber !== dayNumber);

          // Renumerar explícitamente todos los días
          const renumberedDays = filteredDays.map((day, index) => {
            // Calcular el nuevo número de día
            const newDayNumber = index + 1;

            // Actualizar el nombre si es un nombre por defecto
            let updatedName = day.name;
            if (!updatedName || /^Día \d+$/.test(updatedName)) {
              updatedName = `Día ${newDayNumber}`;
            }

            return {
              ...day,
              dayNumber: newDayNumber,
              id: `day-${newDayNumber}`,
              name: updatedName,
            };
          });

          // Verificar que todos los días tienen números únicos y secuenciales
          const dayNumbers = renumberedDays.map((d) => d.dayNumber);
          const hasDuplicates = new Set(dayNumbers).size !== dayNumbers.length;
          const isSequential = dayNumbers.every((num, i) => num === i + 1);

          if (hasDuplicates || !isSequential) {
            // Forzar corrección manual de números
            for (let i = 0; i < renumberedDays.length; i++) {
              renumberedDays[i].dayNumber = i + 1;
              renumberedDays[i].id = `day-${i + 1}`;
            }
          }

          // Actualizar el estado completo del formulario
          const newForm = {
            ...form,
            exerciseDays: renumberedDays,
          };
          setForm(newForm);

          // Limpiar el estado de acordeones para los ejercicios de días eliminados
          const newExpandedExerciseIds = { ...expandedExerciseIds };
          Object.keys(newExpandedExerciseIds).forEach((key) => {
            const [dayIdx] = key.split('-').map(Number);
            // Si el día ya no existe, eliminar sus entradas
            if (dayIdx >= renumberedDays.length) {
              delete newExpandedExerciseIds[key];
            }
          });
          setExpandedExerciseIds(newExpandedExerciseIds);
        } catch (error) {
          // Error al eliminar día
        }
      },
      acceptClassName: 'p-button-danger p-button-text',
      rejectClassName: 'p-button-text',
      draggable: false,
      resizable: false,
    });
  };

  // Función para manejar el comienzo de la edición del nombre del día
  const handleStartEditingDayName = (day: ExerciseDay, dayIndex: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Guardar el ID del día que se está editando
    setEditingDayId(`day-${day.dayNumber}`);
    // Establecer el nombre actual como valor inicial
    const currentName = day.name || `Día ${day.dayNumber}`;
    setEditingDayName(currentName);
  };

  // Función para manejar el fin de la edición del nombre del día
  const handleFinishEditingDayName = (dayIndex: number, e?: React.FocusEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (editingDayName.trim()) {
      // Verificar si el nombre realmente ha cambiado
      const currentName = form.exerciseDays[dayIndex].name || `Día ${form.exerciseDays[dayIndex].dayNumber}`;

      if (editingDayName !== currentName) {
        // Crear una copia profunda completa del estado del formulario
        const newForm = {
          ...form,
          exerciseDays: form.exerciseDays.map((day, idx) => {
            if (idx === dayIndex) {
              return {
                ...day,
                name: editingDayName,
              };
            }
            return { ...day };
          }),
        };

        // Actualizar el formulario completo para garantizar la detección de cambios
        setForm(newForm);
      }
    }

    setEditingDayId(null);
    setEditingDayName('');
  };

  // Función para manejar el cambio del input del nombre del día
  const handleDayNameChange = (value: string) => {
    setEditingDayName(value);
  };

  // Función para manejar la tecla Enter en el input del nombre del día
  const handleDayNameKeyDown = (dayIndex: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFinishEditingDayName(dayIndex, e);
    }
  };

  return {
    editingDayId,
    setEditingDayId,
    editingDayName,
    setEditingDayName,
    handleAddDay,
    handleRemoveDay,
    handleStartEditingDayName,
    handleFinishEditingDayName,
    handleDayNameChange,
    handleDayNameKeyDown,
  };
};
